<template>
  <div class="message-token-editor">
    <div
      ref="editor"
      class="message-token-editor__surface"
      :class="{
        'message-token-editor__surface--disabled': disabled,
        'message-token-editor__surface--error': normalizedErrorMessages.length,
        'message-token-editor__surface--focused': focused
      }"
      :contenteditable="!disabled"
      :data-placeholder="placeholder"
      role="textbox"
      aria-multiline="true"
      @click="handleClick"
      @focus="handleFocus"
      @blur="handleBlur"
      @beforeinput="handleBeforeInput"
      @input="handleInput"
      @keydown="handleKeydown"
      @keyup="saveSelection"
      @mouseup="saveSelection"
      @paste.prevent="handlePaste"
      @dragover.prevent
      @drop.prevent="handleDrop"
    />

    <div v-if="normalizedErrorMessages.length" class="v-messages theme--light error--text mt-1">
      <div class="v-messages__wrapper">
        <div
          v-for="message in normalizedErrorMessages"
          :key="message"
          class="v-messages__message"
        >
          {{ message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MessageTokenEditor",

  props: {
    value: {
      type: String,
      default: ''
    },
    variables: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [String, Array],
      default: ''
    },
    placeholder: {
      type: String,
      default: '輸入訊息內容'
    }
  },

  data() {
    return {
      focused: false,
      lastRange: null,
      lastEmittedValue: null,
    }
  },

  computed: {
    normalizedErrorMessages() {
      if (!this.errorMessages) return []
      return Array.isArray(this.errorMessages) ? this.errorMessages : [this.errorMessages]
    },
    variableMap() {
      return this.variables.reduce((map, variable) => {
        map[variable.value] = variable.label
        return map
      }, {})
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.$nextTick(() => {
          if (value === this.lastEmittedValue) return
          this.renderFromValue(value || '')
        })
      }
    },
    variables() {
      this.$nextTick(() => {
        this.renderFromValue(this.value || '')
      })
    }
  },

  methods: {
    insertToken(token) {
      if (this.disabled) {
        this.$emit('disabled-click')
        return
      }

      const editor = this.$refs.editor
      if (!editor) return

      const range = this.getActiveRange()
      const insertNodes = this.getTokenInsertParts(token, range).map(part => (
        part.type === 'chip'
          ? this.createChip(part.value)
          : document.createTextNode(part.value)
      ))
      const fragment = document.createDocumentFragment()

      range.deleteContents()
      insertNodes.forEach(node => fragment.appendChild(node))
      range.insertNode(fragment)

      const lastNode = insertNodes[insertNodes.length - 1]
      range.setStartAfter(lastNode)
      range.collapse(true)

      this.focusRange(range)
      this.syncFromDom()
    },
    renderFromValue(content) {
      const editor = this.$refs.editor
      if (!editor) return

      editor.innerHTML = ''
      this.parseContent(content).forEach(node => {
        if (node.type === 'chip') {
          editor.appendChild(this.createChip(node.value))
        } else if (node.value) {
          editor.appendChild(document.createTextNode(node.value))
        }
      })
    },
    parseContent(content) {
      const tokens = this.variables.map(variable => variable.value).filter(Boolean)
      if (!tokens.length) return [{ type: 'text', value: content }]

      const regex = new RegExp(`(${tokens.map(this.escapeRegExp).join('|')})`, 'g')
      const nodes = []
      let lastIndex = 0
      let match

      while ((match = regex.exec(content)) !== null) {
        if (match.index > lastIndex) {
          nodes.push({ type: 'text', value: content.slice(lastIndex, match.index) })
        }
        nodes.push({ type: 'chip', value: match[0] })
        lastIndex = match.index + match[0].length
      }

      if (lastIndex < content.length) {
        nodes.push({ type: 'text', value: content.slice(lastIndex) })
      }

      return nodes.length ? nodes : [{ type: 'text', value: '' }]
    },
    createChip(token) {
      const chip = document.createElement('span')
      chip.setAttribute('contenteditable', 'false')
      chip.setAttribute('data-token', token)
      chip.className = `message-token-editor__chip ${this.getTokenClass(token)}`

      const label = document.createElement('span')
      label.textContent = this.getTokenLabel(token)
      chip.appendChild(label)

      return chip
    },
    getTokenLabel(token) {
      return this.variableMap[token] || token
    },
    getTokenClass(token) {
      if (token.includes('clinic_name')) return 'message-token-editor__chip--clinic'
      if (token.includes('appointment_url')) return 'message-token-editor__chip--url'
      return 'message-token-editor__chip--default'
    },
    syncFromDom(options = {}) {
      const editor = this.$refs.editor
      if (!editor) return

      const currentOffset = this.getSelectionContentOffset()
      let content = this.nodesToContent(editor.childNodes)
      let nextOffset = currentOffset

      if (options.normalizeUrlSpacing) {
        const normalized = this.normalizeAppointmentUrlSpacing(content, currentOffset)
        content = normalized.content
        nextOffset = normalized.offset

        if (normalized.changed) {
          this.renderFromValue(content)
          this.$nextTick(() => {
            this.setCaretByContentOffset(nextOffset)
          })
        }
      }

      this.lastEmittedValue = content
      this.$emit('input', content)
      this.saveSelection()
    },
    nodeToContent(node) {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent || ''
      if (node.nodeType !== Node.ELEMENT_NODE) return ''

      const element = node
      const token = element.getAttribute('data-token')
      if (token) return token
      if (element.tagName === 'BR') return '\n'

      return this.nodesToContent(element.childNodes)
    },
    nodesToContent(nodes) {
      return Array.from(nodes).reduce((content, node, index, allNodes) => {
        const nodeContent = this.nodeToContent(node)
        if (!this.isBlockContentNode(node)) return content + nodeContent

        let nextContent = content
        if (nextContent && !nextContent.endsWith('\n')) nextContent += '\n'
        nextContent += nodeContent
        if (index < allNodes.length - 1 && !nextContent.endsWith('\n')) nextContent += '\n'
        return nextContent
      }, '')
    },
    isBlockContentNode(node) {
      if (node.nodeType !== Node.ELEMENT_NODE) return false
      return ['DIV', 'P', 'LI'].includes(node.tagName)
    },
    fragmentToContent(fragment) {
      return this.nodesToContent(fragment.childNodes)
    },
    getRangeContentBefore(range) {
      const editor = this.$refs.editor
      const beforeRange = document.createRange()
      beforeRange.selectNodeContents(editor)
      beforeRange.setEnd(range.startContainer, range.startOffset)
      return this.fragmentToContent(beforeRange.cloneContents())
    },
    getRangeContentAfter(range) {
      const editor = this.$refs.editor
      const afterRange = document.createRange()
      afterRange.selectNodeContents(editor)
      afterRange.setStart(range.endContainer, range.endOffset)
      return this.fragmentToContent(afterRange.cloneContents())
    },
    getTokenInsertParts(token, range) {
      const parts = []
      const shouldPadUrl = token === '#appointment_url#'

      if (shouldPadUrl && this.shouldAddSpaceBefore(this.getRangeContentBefore(range))) {
        parts.push({ type: 'text', value: ' ' })
      }

      parts.push({ type: 'chip', value: token })

      if (shouldPadUrl && this.shouldAddSpaceAfter(this.getRangeContentAfter(range))) {
        parts.push({ type: 'text', value: ' ' })
      }

      return parts
    },
    shouldAddSpaceBefore(content) {
      const previousCharacter = content.slice(-1)
      return Boolean(previousCharacter) && !/\s/.test(previousCharacter)
    },
    shouldAddSpaceAfter(content) {
      const nextCharacter = content.charAt(0)
      return Boolean(nextCharacter) && !/\s/.test(nextCharacter)
    },
    handleInput() {
      this.syncFromDom({ normalizeUrlSpacing: true })
    },
    handleClick() {
      if (this.disabled) {
        this.$emit('disabled-click')
        return
      }

      this.saveSelection()
    },
    handleBeforeInput(event) {
      if (this.disabled || event.inputType !== 'insertText' || !this.shouldAddSpaceBeforeInput(event.data)) return

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) return

      const range = selection.getRangeAt(0)
      if (!range.collapsed || !this.isRangeInsideEditor(range)) return
      if (!this.isCaretImmediatelyAfterToken(range, '#appointment_url#')) return

      event.preventDefault()
      this.insertTextAtRange(` ${event.data}`, range)
    },
    shouldAddSpaceBeforeInput(value) {
      return Boolean(value) && !/^[\s，。！？、；：,.!?;:)）】》」』]$/.test(value)
    },
    shouldSeparateUrlFromCharacter(value) {
      return Boolean(value) && !/^[\s，。！？、；：,.!?;:)）】》」』]$/.test(value)
    },
    normalizeAppointmentUrlSpacing(content, offset) {
      const token = '#appointment_url#'
      let nextContent = ''
      let nextOffset = offset
      let cursor = 0
      let changed = false

      while (cursor < content.length) {
        const tokenIndex = content.indexOf(token, cursor)
        if (tokenIndex === -1) {
          nextContent += content.slice(cursor)
          break
        }

        nextContent += content.slice(cursor, tokenIndex + token.length)

        const nextCharacterIndex = tokenIndex + token.length
        const nextCharacter = content.charAt(nextCharacterIndex)
        if (this.shouldSeparateUrlFromCharacter(nextCharacter)) {
          nextContent += ' '
          changed = true
          if (nextCharacterIndex <= offset) nextOffset += 1
        }

        cursor = nextCharacterIndex
      }

      return {
        content: nextContent,
        offset: nextOffset,
        changed,
      }
    },
    isCaretImmediatelyAfterToken(range, token) {
      const previousNode = this.getPreviousCaretNode(range)
      return previousNode?.nodeType === Node.ELEMENT_NODE &&
        previousNode.getAttribute?.('data-token') === token
    },
    getPreviousCaretNode(range) {
      const editor = this.$refs.editor
      if (range.startContainer === editor) {
        return editor.childNodes[range.startOffset - 1]
      }

      if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
        return range.startContainer.previousSibling
      }

      return null
    },
    insertTextAtRange(text, range) {
      const textNode = document.createTextNode(text)

      range.deleteContents()
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)

      this.focusRange(range)
      this.syncFromDom()
    },
    handleFocus() {
      this.focused = true
      this.saveSelection()
    },
    handleBlur() {
      this.focused = false
      this.saveSelection()
    },
    handleKeydown(event) {
      if (this.disabled) return
      if (event.key === 'Backspace') this.removeAdjacentChip(event, 'previous')
      if (event.key === 'Delete') this.removeAdjacentChip(event, 'next')
    },
    removeAdjacentChip(event, direction) {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) return

      const range = selection.getRangeAt(0)
      if (!range.collapsed || !this.isRangeInsideEditor(range)) return

      const tokenPosition = this.findTokenPositionForDeletion(range, direction)
      if (!tokenPosition) return

      const caretOffset = tokenPosition.start
      event.preventDefault()
      tokenPosition.chip.remove()
      this.setCaretByContentOffset(caretOffset)
      this.syncFromDom()
    },
    findTokenPositionForDeletion(range, direction) {
      const containingChip = this.getContainingTokenChip(range.startContainer)
      if (containingChip) {
        const start = this.getContentOffsetBeforeNode(containingChip)
        return {
          chip: containingChip,
          start,
          end: start + this.nodeToContent(containingChip).length,
        }
      }

      const offset = this.getSelectionContentOffset()
      return this.getTokenChipPositions().find(position => (
        direction === 'previous'
          ? position.end === offset
          : position.start === offset
      ))
    },
    getContainingTokenChip(node) {
      const editor = this.$refs.editor
      if (!editor || !node || node === editor) return null

      const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
      const chip = element?.closest?.('[data-token]')
      return chip && editor.contains(chip) ? chip : null
    },
    getTokenChipPositions() {
      const editor = this.$refs.editor
      if (!editor) return []

      return Array.from(editor.querySelectorAll('[data-token]')).map(chip => {
        const start = this.getContentOffsetBeforeNode(chip)
        return {
          chip,
          start,
          end: start + this.nodeToContent(chip).length,
        }
      })
    },
    getContentOffsetBeforeNode(node) {
      const editor = this.$refs.editor
      if (!editor) return 0

      const range = document.createRange()
      range.selectNodeContents(editor)
      range.setEndBefore(node)
      return this.fragmentToContent(range.cloneContents()).length
    },
    handlePaste(event) {
      if (this.disabled) return

      const text = event.clipboardData.getData('text/plain')
      const range = this.getActiveRange()
      this.insertTextAtRange(text, range)
    },
    handleDrop(event) {
      if (this.disabled) {
        this.$emit('disabled-click')
        return
      }

      const token = event.dataTransfer.getData('text/plain')
      if (!this.variableMap[token]) return

      const range = this.getRangeFromPoint(event.clientX, event.clientY)
      this.focusRange(range)
      this.insertToken(token)
    },
    getRangeFromPoint(x, y) {
      if (document.caretRangeFromPoint) {
        const range = document.caretRangeFromPoint(x, y)
        if (range && this.isRangeInsideEditor(range)) return range
      }
      const position = document.caretPositionFromPoint?.(x, y)
      if (position) {
        const range = document.createRange()
        range.setStart(position.offsetNode, position.offset)
        range.collapse(true)
        if (this.isRangeInsideEditor(range)) return range
      }
      return this.getActiveRange()
    },
    getActiveRange() {
      const editor = this.$refs.editor
      const selection = window.getSelection()

      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        if (this.isRangeInsideEditor(range)) return range
      }

      if (this.lastRange) {
        try {
          if (this.isRangeInsideEditor(this.lastRange)) return this.lastRange
        } catch (e) {
          this.lastRange = null
        }
      }

      const range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(false)
      return range
    },
    focusRange(range) {
      const editor = this.$refs.editor
      const selection = window.getSelection()
      if (!selection) return

      editor.focus()
      selection.removeAllRanges()
      selection.addRange(range)
      this.lastRange = range.cloneRange()
    },
    getSelectionContentOffset() {
      const editor = this.$refs.editor
      const selection = window.getSelection()
      if (!editor || !selection || selection.rangeCount === 0) return 0

      const range = selection.getRangeAt(0)
      if (!this.isRangeInsideEditor(range)) return 0

      const beforeRange = document.createRange()
      beforeRange.selectNodeContents(editor)
      beforeRange.setEnd(range.endContainer, range.endOffset)
      return this.fragmentToContent(beforeRange.cloneContents()).length
    },
    setCaretByContentOffset(offset) {
      const editor = this.$refs.editor
      if (!editor) return

      const range = document.createRange()
      const position = this.findCaretPositionByContentOffset(editor, offset)
      range.setStart(position.node, position.offset)
      range.collapse(true)
      this.focusRange(range)
    },
    findCaretPositionByContentOffset(parent, offset) {
      let remaining = offset
      let fallback = { node: parent, offset: parent.childNodes.length }

      for (let index = 0; index < parent.childNodes.length; index += 1) {
        const child = parent.childNodes[index]
        const childLength = this.nodeToContent(child).length

        if (child.nodeType === Node.TEXT_NODE) {
          if (remaining <= childLength) {
            return { node: child, offset: remaining }
          }
        } else if (remaining <= childLength) {
          return { node: parent, offset: index + 1 }
        }

        remaining -= childLength
        fallback = { node: parent, offset: index + 1 }
      }

      return fallback
    },
    saveSelection() {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) return

      const range = selection.getRangeAt(0)
      if (!this.isRangeInsideEditor(range)) return

      this.lastRange = range.cloneRange()
    },
    isRangeInsideEditor(range) {
      const editor = this.$refs.editor
      if (!editor) return false

      const container = range.commonAncestorContainer
      return container === editor || editor.contains(container)
    },
    escapeRegExp(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }
  }
}
</script>

<style lang="scss">
.message-token-editor__surface {
  min-height: 116px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 12px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
  background: #fff;
  outline: none;
}

.message-token-editor__surface:empty::before {
  color: rgba(0, 0, 0, 0.38);
  content: attr(data-placeholder);
  pointer-events: none;
}

.message-token-editor__surface--focused {
  border-color: var(--v-primary-base);
  border-width: 2px;
  padding: 11px;
}

.message-token-editor__surface--error {
  border-color: var(--v-error-base);
}

.message-token-editor__surface--disabled {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.38);
  cursor: default;
}

.message-token-editor__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 2px;
  padding: 1px 9px;
  border-radius: 999px;
  border: 1px solid currentColor;
  font-weight: 500;
  line-height: 1.4;
  user-select: none;
  vertical-align: baseline;
  cursor: pointer;
}

.message-token-editor__chip--clinic {
  color: #00897b;
  background: #e0f2f1;
}

.message-token-editor__chip--url {
  color: #1565c0;
  background: #e3f2fd;
}

.message-token-editor__chip--default {
  color: #00897b;
  background: #e0f7fa;
}

</style>
